import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, withKeys as _withKeys, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["onKeydown"]
const _hoisted_2 = {
  key: 0,
  viewBox: "0 0 24 24",
  width: "26",
  height: "26"
}
const _hoisted_3 = {
  key: 1,
  viewBox: "0 0 24 24",
  width: "26",
  height: "26"
}
const _hoisted_4 = {
  key: 2,
  viewBox: "0 0 24 24",
  width: "26",
  height: "26"
}
const _hoisted_5 = { class: "mas-subtitle" }
const _hoisted_6 = {
  class: "mas-timeline",
  "aria-hidden": "true"
}
const _hoisted_7 = { class: "mas-tl-node" }
const _hoisted_8 = {
  key: 0,
  viewBox: "0 0 24 24",
  width: "12",
  height: "12"
}
const _hoisted_9 = {
  key: 1,
  class: "mas-tl-dot"
}
const _hoisted_10 = { class: "mas-tl-label" }
const _hoisted_11 = { class: "mas-body" }

import { computed, ref, watch, nextTick } from 'vue';
import type { Phase } from '@/composables/useMentorApplicationStatus';
import { getRejectionTitle } from '@/shared/lib/mentorRejection';

interface Props {
    modelValue: boolean;
    phase: Phase;
    rejectionReason?: string;
}
type Tone = 'pending' | 'success' | 'info';
type TlState = 'done' | 'current' | 'upcoming';


export default /*@__PURE__*/_defineComponent({
  __name: 'MentorApplicationStatusModal',
  props: {
    modelValue: { type: Boolean },
    phase: {},
    rejectionReason: { default: '' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const primaryBtn = ref<HTMLButtonElement | null>(null);
const titleId = `mas-title-${Math.random().toString(36).slice(2, 9)}`;

function close(): void {
    emit('update:modelValue', false);
}

// Фокус на главную кнопку при открытии (a11y).
watch(
    () => props.modelValue,
    async (open) => {
        if (open) {
            await nextTick();
            primaryBtn.value?.focus();
        }
    },
);

// ── Контент по фазе (основное — pending) ──────────────────────────────

const tone = computed<Tone>(() => {
    if (props.phase === 'approved') return 'success';
    if (props.phase === 'pending' || props.phase === 'changes_requested') return 'pending';
    return 'info';
});

const title = computed<string>(() => {
    switch (props.phase) {
        case 'pending':
            return 'Заявка на проверке';
        case 'changes_requested':
            return 'Нужны правки заявки';
        case 'approved':
            return 'Вы — наставник';
        case 'rejected':
            return 'Заявка отклонена';
        case 'draft':
            return 'Заявка не отправлена';
        default:
            return 'Статус заявки наставника';
    }
});

const subtitle = computed<string>(() => {
    switch (props.phase) {
        case 'pending':
            return 'Мы проверяем вашу заявку наставника';
        case 'changes_requested':
            return 'Модератор вернул заявку на доработку';
        case 'approved':
            return 'Профиль наставника подтверждён';
        case 'rejected':
            return 'К сожалению, заявку не одобрили';
        case 'draft':
            return 'Вы начали, но ещё не отправили заявку';
        default:
            return '';
    }
});

/**
 * Человекочитаемая причина отказа. Контейнер прокидывает сырой
 * `rejection_reason` (TextChoices code, напр. "low_quality"); мапим его через
 * тот же shared-lookup, что и баннер на /become-mentor и dashboard-карточка,
 * чтобы не печатать машинный код. Неизвестный код (или уже человекочитаемая
 * строка от старого вызывающего кода) проходит как есть — fallback не ломает
 * существующих потребителей.
 */
const rejectionReasonText = computed<string>(() => {
    const raw = props.rejectionReason?.trim();
    if (!raw) return '';
    return getRejectionTitle(raw) || raw;
});

const bodyText = computed<string>(() => {
    switch (props.phase) {
        case 'pending':
            return 'Обычно проверка занимает до 3 рабочих дней. Как только примем решение — пришлём уведомление, ничего делать не нужно.';
        case 'changes_requested':
            return 'Откройте заявку, дополните недостающие поля и отправьте её снова.';
        case 'approved':
            return 'Теперь вы можете проводить сессии. Привяжите карту для выплат, чтобы получать за них оплату.';
        case 'rejected':
            return rejectionReasonText.value
                ? `Причина: ${rejectionReasonText.value}. Поправьте профиль и подайте заявку заново.`
                : 'Поправьте профиль и подайте заявку заново.';
        case 'draft':
            return 'Вернитесь к заявке и заполните её до конца, чтобы отправить на модерацию.';
        default:
            return '';
    }
});

const note = computed<string>(() => {
    if (
        props.phase === 'pending' ||
        props.phase === 'changes_requested' ||
        props.phase === 'draft'
    ) {
        return 'Пока заявка не одобрена, платные сессии недоступны — карту для выплат можно будет привязать после подтверждения.';
    }
    return '';
});

/** Мини-таймлайн «подана → проверка → решение», состояние по фазе. */
const timeline = computed<Array<{ key: string; label: string; state: TlState }>>(() => {
    const submitted: TlState = props.phase === 'draft' ? 'current' : 'done';
    const review: TlState =
        props.phase === 'pending' || props.phase === 'changes_requested'
            ? 'current'
            : props.phase === 'approved' || props.phase === 'rejected'
              ? 'done'
              : 'upcoming';
    const decision: TlState =
        props.phase === 'approved' || props.phase === 'rejected' ? 'done' : 'upcoming';
    return [
        { key: 'submitted', label: 'Подана', state: submitted },
        { key: 'review', label: 'Проверка', state: review },
        {
            key: 'decision',
            label:
                props.phase === 'approved'
                    ? 'Одобрена'
                    : props.phase === 'rejected'
                      ? 'Отклонена'
                      : 'Решение',
            state: decision,
        },
    ];
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "mas-fade" }, {
      default: _withCtx(() => [
        (_ctx.modelValue)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "mas-overlay",
              "data-testid": "mentor-status-modal",
              onClick: _withModifiers(close, ["self"]),
              onKeydown: _withKeys(_withModifiers(close, ["stop","prevent"]), ["esc"])
            }, [
              _createVNode(_Transition, {
                name: "mas-pop",
                appear: ""
              }, {
                default: _withCtx(() => [
                  (_ctx.modelValue)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "mas-card",
                        role: "dialog",
                        "aria-modal": "true",
                        "aria-labelledby": titleId,
                        tabindex: "-1"
                      }, [
                        _createElementVNode("button", {
                          type: "button",
                          class: "mas-close",
                          "aria-label": "Закрыть",
                          "data-testid": "mentor-status-modal-close",
                          onClick: close
                        }, _cache[0] || (_cache[0] = [
                          _createElementVNode("svg", {
                            viewBox: "0 0 24 24",
                            width: "20",
                            height: "20",
                            "aria-hidden": "true"
                          }, [
                            _createElementVNode("path", {
                              d: "M6 6l12 12M18 6L6 18",
                              fill: "none",
                              stroke: "currentColor",
                              "stroke-width": "2",
                              "stroke-linecap": "round"
                            })
                          ], -1)
                        ])),
                        _createElementVNode("div", {
                          class: _normalizeClass(["mas-icon", `mas-icon--${tone.value}`]),
                          "aria-hidden": "true"
                        }, [
                          (tone.value === 'pending')
                            ? (_openBlock(), _createElementBlock("svg", _hoisted_2, _cache[1] || (_cache[1] = [
                                _createElementVNode("circle", {
                                  cx: "12",
                                  cy: "12",
                                  r: "9",
                                  fill: "none",
                                  stroke: "currentColor",
                                  "stroke-width": "2"
                                }, null, -1),
                                _createElementVNode("path", {
                                  d: "M12 7.5V12l3 2",
                                  fill: "none",
                                  stroke: "currentColor",
                                  "stroke-width": "2",
                                  "stroke-linecap": "round",
                                  "stroke-linejoin": "round"
                                }, null, -1)
                              ])))
                            : (tone.value === 'success')
                              ? (_openBlock(), _createElementBlock("svg", _hoisted_3, _cache[2] || (_cache[2] = [
                                  _createElementVNode("path", {
                                    d: "M5 13l4 4L19 7",
                                    fill: "none",
                                    stroke: "currentColor",
                                    "stroke-width": "2.5",
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round"
                                  }, null, -1)
                                ])))
                              : (_openBlock(), _createElementBlock("svg", _hoisted_4, _cache[3] || (_cache[3] = [
                                  _createElementVNode("path", {
                                    d: "M12 8v5M12 16.5v.5",
                                    fill: "none",
                                    stroke: "currentColor",
                                    "stroke-width": "2",
                                    "stroke-linecap": "round"
                                  }, null, -1),
                                  _createElementVNode("circle", {
                                    cx: "12",
                                    cy: "12",
                                    r: "9",
                                    fill: "none",
                                    stroke: "currentColor",
                                    "stroke-width": "2"
                                  }, null, -1)
                                ])))
                        ], 2),
                        _createElementVNode("h2", {
                          id: titleId,
                          class: "mas-title"
                        }, _toDisplayString(title.value), 1),
                        _createElementVNode("p", _hoisted_5, _toDisplayString(subtitle.value), 1),
                        _createElementVNode("ol", _hoisted_6, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timeline.value, (t, i) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: t.key,
                              class: _normalizeClass(["mas-tl-step", [`is-${t.state}`, { 'has-line': i > 0 }]])
                            }, [
                              (i > 0)
                                ? (_openBlock(), _createElementBlock("span", {
                                    key: 0,
                                    class: _normalizeClass(["mas-tl-line", { 'is-filled': timeline.value[i - 1].state === 'done' }])
                                  }, null, 2))
                                : _createCommentVNode("", true),
                              _createElementVNode("span", _hoisted_7, [
                                (t.state === 'done')
                                  ? (_openBlock(), _createElementBlock("svg", _hoisted_8, _cache[4] || (_cache[4] = [
                                      _createElementVNode("path", {
                                        d: "M5 13l4 4L19 7",
                                        fill: "none",
                                        stroke: "currentColor",
                                        "stroke-width": "2.5",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                      }, null, -1)
                                    ])))
                                  : (_openBlock(), _createElementBlock("span", _hoisted_9))
                              ]),
                              _createElementVNode("span", _hoisted_10, _toDisplayString(t.label), 1)
                            ], 2))
                          }), 128))
                        ]),
                        _createElementVNode("p", _hoisted_11, _toDisplayString(bodyText.value), 1),
                        (note.value)
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              class: _normalizeClass(["mas-note", `mas-note--${tone.value}`])
                            }, _toDisplayString(note.value), 3))
                          : _createCommentVNode("", true),
                        _createElementVNode("button", {
                          ref_key: "primaryBtn",
                          ref: primaryBtn,
                          type: "button",
                          class: "mas-primary",
                          "data-testid": "mentor-status-modal-ok",
                          onClick: close
                        }, " Понятно ", 512)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ], 40, _hoisted_1))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})